import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import EditableText from '@/components/editable/EditableText'
import { useSiteEdit } from '@/contexts/SiteEditContext'

const Contact: React.FC = () => {
  const { get } = useSiteEdit()
  const [form, setForm] = useState({ name: '', email: '', role: '', message: '' })
  const [sending, setSending] = useState(false)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name || !form.email || !form.message) {
      toast.error('Merci de compléter les champs requis.')
      return
    }
    setSending(true)
    try {
      await lumi.functions.invoke('contactForm', {
        method: 'POST',
        body: {
          name: form.name,
          email: form.email,
          role: form.role,
          message: form.message,
        },
      })
      toast.success('Message envoyé. Nous revenons vers vous.')
      setForm({ name: '', email: '', role: '', message: '' })
    } catch (error: any) {
      const msg = error?.data?.error || 'Une erreur est survenue. Veuillez réessayer.'
      toast.error(msg)
    } finally {
      setSending(false)
    }
  }

  return (
    <section id="contact" className="relative bg-[#0E1B2C] py-32 lg:py-44 overflow-hidden">
      <div
        className="absolute -bottom-32 -left-32 w-[600px] h-[600px] pointer-events-none blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(184,194,107,0.12), transparent 70%)' }}
      />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left intro */}
          <div className="lg:col-span-5 reveal">
            <div className="text-[10px] tracking-luxe text-[#C8A24A] mb-4">07 — CONTACT</div>
            <div className="divider-gold w-24 mb-8" />
            <h3 className="font-serif-display text-[40px] sm:text-[52px] lg:text-[68px] leading-[1.02] text-[#E8DCC4] text-balance mb-8">
              <EditableText contentKey="contact.title" section="Contact" label="Titre" multiline as="span">
                Prenons contact.
              </EditableText>
            </h3>
            <p className="text-[16px] leading-[1.75] text-[#C9BFA8] mb-12 max-w-md">
              <EditableText contentKey="contact.intro" section="Contact" label="Introduction" multiline as="span">
                Vous portez un projet, une expertise, une intention de mécénat ou simplement de la curiosité ? Écrivez-nous — nous lisons chaque message.
              </EditableText>
            </p>

            <div className="space-y-6">
              <div>
                <div className="text-[10px] tracking-luxe text-[#C8A24A] mb-1"><EditableText contentKey="contact.label.email" section="Contact" label="Étiquette — Email" as="span">EMAIL</EditableText></div>
                <a
                  href="mailto:sylvie@associationcentredetransformation.ch"
                  className="text-[16px] text-[#E8DCC4] hover:text-[#C9E26A] transition-colors"
                >
                  sylvie@associationcentredetransformation.ch
                </a>
              </div>
              <div>
                <div className="text-[10px] tracking-luxe text-[#C8A24A] mb-1"><EditableText contentKey="contact.label.ancrage" section="Contact" label="Étiquette — Ancrage" as="span">ANCRAGE</EditableText></div>
                <div className="text-[15px] text-[#E8DCC4]"><EditableText contentKey="contact.ancrage" section="Contact" label="Ancrage géographique" as="span">Suisse · Genève · Gryon · Narbonne</EditableText></div>
              </div>
              <div>
                <div className="text-[10px] tracking-luxe text-[#C8A24A] mb-1"><EditableText contentKey="contact.label.reseaux" section="Contact" label="Étiquette — Réseaux" as="span">RÉSEAUX</EditableText></div>
                <div className="flex items-center gap-4 text-[12px] tracking-mid uppercase text-[#C9BFA8]">
                  <a href="#" className="hover:text-[#C9E26A]"><EditableText contentKey="contact.social.instagram" section="Contact" label="Réseau — Instagram" as="span">Instagram</EditableText></a>
                  <span className="w-1 h-1 rounded-full bg-[#C9BFA8]/40" />
                  <a href="#" className="hover:text-[#C9E26A]"><EditableText contentKey="contact.social.linkedin" section="Contact" label="Réseau — LinkedIn" as="span">LinkedIn</EditableText></a>
                  <span className="w-1 h-1 rounded-full bg-[#C9BFA8]/40" />
                  <a href="#" className="hover:text-[#C9E26A]"><EditableText contentKey="contact.social.youtube" section="Contact" label="Réseau — YouTube" as="span">YouTube</EditableText></a>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 reveal">
            <form onSubmit={onSubmit} className="hairline-strong p-8 lg:p-12 bg-[#14253B]/40 backdrop-blur-sm">
              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <Field
                  label="Nom *"
                  contentKey="contact.field.name.label"
                  value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })}
                />
                <Field
                  label="Email *"
                  contentKey="contact.field.email.label"
                  type="email"
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                />
              </div>
              <div className="mb-6">
                <label className="block text-[10px] tracking-luxe text-[#C8A24A] mb-3">
                  <EditableText contentKey="contact.field.role.label" section="Contact" label="Champ - Vous êtes" as="span">
                    VOUS Êtes
                  </EditableText>
                </label>
                <input
                  type="text"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  placeholder={get('contact.field.role.placeholder', 'Mécène · Institution · Entreprise · Intervenant · Autre')}
                  className="w-full bg-transparent border-b border-[rgba(232,220,196,0.20)] focus:border-[#C9E26A] outline-none py-3 text-[15px] text-[#E8DCC4] placeholder:text-[#C9BFA8]/40 transition-colors"
                />
              </div>
              <div className="mb-8">
                <label className="block text-[10px] tracking-luxe text-[#C8A24A] mb-3">
                  <EditableText contentKey="contact.field.message.label" section="Contact" label="Champ — Message" as="span">
                    MESSAGE *
                  </EditableText>
                </label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-transparent border-b border-[rgba(232,220,196,0.20)] focus:border-[#C9E26A] outline-none py-3 text-[15px] text-[#E8DCC4] placeholder:text-[#C9BFA8]/40 transition-colors resize-none"
                  placeholder={get('contact.field.message.placeholder', 'Parlez-nous de votre projet, votre intention, votre question…')}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-6">
                <p className="text-[11px] text-[#C9BFA8]/70 max-w-xs">
                  <EditableText contentKey="contact.form.privacy" section="Contact" label="Note confidentialité" multiline as="span">
                    Vos données ne sont utilisées que pour répondre à votre demande.
                  </EditableText>
                </p>
                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center gap-3 px-8 py-4 text-[12px] tracking-mid uppercase text-[#0E1B2C] bg-[#C9E26A] hover:bg-[#E8DCC4] disabled:opacity-60 transition-colors duration-300"
                >
                  {sending ? get('contact.form.sending', 'Envoi en cours…') : get('contact.form.submit', 'Envoyer le message')}
                  <span className="w-6 h-px bg-[#0E1B2C]" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}

interface FieldProps {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
  contentKey?: string
}

const Field: React.FC<FieldProps> = ({ label, value, onChange, type = 'text', placeholder, contentKey }) => (
  <div>
    <label className="block text-[10px] tracking-luxe text-[#C8A24A] mb-3">
      {contentKey ? (
        <EditableText contentKey={contentKey} section="Contact" label={`Champ — ${label}`} as="span">
          {label.toUpperCase()}
        </EditableText>
      ) : (
        label.toUpperCase()
      )}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-transparent border-b border-[rgba(232,220,196,0.20)] focus:border-[#C9E26A] outline-none py-3 text-[15px] text-[#E8DCC4] placeholder:text-[#C9BFA8]/40 transition-colors"
    />
  </div>
)

export default Contact
